import api from "utils/api";
import endpoint from "utils/endpoint";

export type TakeoutDiscountRuleType = "percent" | "amount" | "free_shipping";
export type TakeoutDiscountRule = {
  min_amount: number;
  max_amount: number | null;
  type: TakeoutDiscountRuleType;
  value: number | null;
};

// NOTE:
// 管理画面の入力値は文字列として扱われるため、更新APIへは string/null を許容して渡す。
// サーバ側 Normalizer が Integer() で解釈し、バリデーションエラー時は message を返す。
export type TakeoutDiscountRulePayload = {
  min_amount: string | null;
  max_amount: string | null;
  type: TakeoutDiscountRuleType;
  value: string | null;
};

class TakeoutRepository {
  /**
   * 代理店ユーザーのみ利用可能
   * テイクアウト機能を有効化する
   */
  public async enable(isAgency: boolean, storeId: string, useAsEc: boolean) {
    const url = endpoint.agency.takeout.use(storeId);
    return api.post(url, { as_ec: useAsEc });
  }

  public async setEcDefaultShippingFee(
    isAgency: boolean,
    storeId: string,
    ecDefaultShippingFee: number | null
  ) {
    const url =
      isAgency && storeId
        ? endpoint.agency.takeout.ecShippingFee(storeId)
        : endpoint.store.takeout.ecShippingFee();
    return api.post(url, { shipping_fee: ecDefaultShippingFee });
  }

  public async setEcPrefectureShippingFee(
    isAgency: boolean,
    storeId: string,
    ecPrefectureShippingFeesJson: string
  ) {
    const url =
      isAgency && storeId
        ? endpoint.agency.takeout.ecPrefectureShippingFees(storeId)
        : endpoint.store.takeout.ecPrefectureShippingFees();
    return api.post(url, {
      prefecture_shipping_fees_json: ecPrefectureShippingFeesJson,
    });
  }

  public async setEcCustomShippingDestinations(
    isAgency: boolean,
    storeId: string,
    customShippingDestinations: Array<{
      id?: string;
      name: string;
      fee: number | null;
      after_type: "head" | "prefecture" | "custom";
      after_value: number | string | null;
    }>
  ) {
    const url =
      isAgency && storeId
        ? endpoint.agency.takeout.ecCustomShippingDestinations(storeId)
        : endpoint.store.takeout.ecCustomShippingDestinations();
    return api.post(url, {
      custom_shipping_destinations: customShippingDestinations,
    });
  }

  public async setEcHiddenPrefectureCodes(
    isAgency: boolean,
    storeId: string,
    hiddenPrefectureCodes: string[]
  ) {
    const url =
      isAgency && storeId
        ? endpoint.agency.takeout.ecHiddenPrefectureCodes(storeId)
        : endpoint.store.takeout.ecHiddenPrefectureCodes();
    return api.post(url, { hidden_prefecture_codes: hiddenPrefectureCodes });
  }

  public async setEcCodFee(
    isAgency: boolean,
    storeId: string,
    ecCodFee: number | null
  ) {
    const url =
      isAgency && storeId
        ? endpoint.agency.takeout.ecCodFee(storeId)
        : endpoint.store.takeout.ecCodFee();
    return api.post(url, { cod_fee: ecCodFee });
  }

  public async setInvoiceSettings(
    isAgency: boolean,
    storeId: string,
    useInvoice: boolean,
    takeoutIssuerEnterpriseName: string | null,
    takeoutIssuerRegistrationNumber: string | null
  ) {
    const url =
      isAgency && storeId
        ? endpoint.agency.takeout.invoiceSettings(storeId)
        : endpoint.store.takeout.invoiceSettings();
    return api.post(url, {
      use_invoice: useInvoice,
      issuer_name: takeoutIssuerEnterpriseName,
      issuer_number: takeoutIssuerRegistrationNumber,
    });
  }

  public async setTakeoutAndEcCommonSettings(
    isAgency: boolean,
    storeId: string,
    takeoutNotesCart: string,
    takeoutNotesConfirm: string
  ) {
    const url =
      isAgency && storeId
        ? endpoint.agency.takeout.commonSettings(storeId)
        : endpoint.store.takeout.commonSettings();
    return api.post(url, {
      takeout_notes_cart: takeoutNotesCart,
      takeout_notes_confirm: takeoutNotesConfirm,
    });
  }

  public async getTakeoutDiscountRules(isAgency: boolean, storeId: string) {
    const url =
      isAgency && storeId
        ? endpoint.agency.takeout.discountRules(storeId)
        : endpoint.store.takeout.discountRules();
    const resp = await api.get(url);
    return (resp.data?.discount_rules || []) as TakeoutDiscountRule[];
  }

  public async updateTakeoutDiscountRules(
    isAgency: boolean,
    storeId: string,
    discountRules: TakeoutDiscountRulePayload[]
  ) {
    const url =
      isAgency && storeId
        ? endpoint.agency.takeout.discountRules(storeId)
        : endpoint.store.takeout.discountRules();
    return api.post(url, { discount_rules: discountRules });
  }
}

export default new TakeoutRepository();
